import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { type ScrollToBottomOptions, useStickToBottom } from 'use-stick-to-bottom';

interface OpenedChatScroll {
  guid: string | null;
  isLoading: boolean;
  ready: boolean;
  skip: boolean;
}

export function useCasePartnerScroll(isGenerating: boolean, openedChat?: OpenedChatScroll) {
  const scroll = useStickToBottom({
    initial: 'instant',
    resize: {
      damping: 0.72,
      mass: 1.15,
      stiffness: 0.08,
    },
  });
  const isGeneratingRef = useRef(isGenerating);
  const isOneTimeScrollActiveRef = useRef(false);
  const responseScrollQueuedRef = useRef(false);
  const [streamingResponseElement, setStreamingResponseElement] = useState<HTMLDivElement | null>(null);
  const [isStreamingResponseBeyondFold, setIsStreamingResponseBeyondFold] = useState(false);
  isGeneratingRef.current = isGenerating;

  const scrollToBottom = useCallback((options?: ScrollToBottomOptions) => {
    const requestedAnimation = typeof options === 'string' ? options : options?.animation;
    const scrollElement = scroll.scrollRef.current;

    // A user-initiated smooth scroll targets a snapshot of the current bottom,
    // so new tokens do not turn it back into continuous auto-scroll.
    if (isGeneratingRef.current && requestedAnimation === 'smooth' && scrollElement) {
      scroll.stopScroll();
      scrollElement.scrollTo({
        behavior: 'smooth',
        top: scrollElement.scrollHeight,
      });
      return true;
    }

    isOneTimeScrollActiveRef.current = true;
    // While generating, jump to the current bottom instead of starting an
    // animation that would keep chasing every streamed content resize.
    const oneTimeOptions = isGeneratingRef.current
      ? {
          ...(typeof options === 'object' ? options : {}),
          animation: 'instant' as const,
        }
      : options;
    const result = scroll.scrollToBottom(oneTimeOptions);

    void Promise.resolve(result).finally(() => {
      isOneTimeScrollActiveRef.current = false;
      if (isGeneratingRef.current) {
        scroll.stopScroll();
      }
    });

    return result;
  }, [scroll.scrollRef, scroll.scrollToBottom, scroll.stopScroll]);

  // Queue this before sending so the scroll happens after React has rendered
  // the new user message and the temporary response space.
  const scrollToResponseStart = useCallback(() => {
    responseScrollQueuedRef.current = true;
  }, []);

  const streamingResponseRef = useCallback((element: HTMLDivElement | null) => {
    setStreamingResponseElement(element);
  }, []);

  useLayoutEffect(() => {
    const scrollElement = scroll.scrollRef.current;

    if (!isGenerating || !streamingResponseElement || !scrollElement) {
      setIsStreamingResponseBeyondFold(false);
      return;
    }

    const updateResponsePosition = () => {
      const responseRect = streamingResponseElement.getBoundingClientRect();
      const scrollRect = scrollElement.getBoundingClientRect();
      const nextIsBeyondFold = responseRect.bottom > scrollRect.bottom;
      setIsStreamingResponseBeyondFold(current => (
        current === nextIsBeyondFold ? current : nextIsBeyondFold
      ));
    };

    updateResponsePosition();
    scrollElement.addEventListener('scroll', updateResponsePosition, { passive: true });

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateResponsePosition);
    resizeObserver?.observe(streamingResponseElement);

    return () => {
      scrollElement.removeEventListener('scroll', updateResponsePosition);
      resizeObserver?.disconnect();
    };
  }, [isGenerating, scroll.scrollRef, streamingResponseElement]);

  const openedChatGuidRef = useRef<string | null>(null);
  const openedChatGuid = openedChat?.guid ?? null;
  const openedChatLoading = openedChat?.isLoading ?? false;
  const openedChatReady = openedChat?.ready ?? false;
  const openedChatSkip = openedChat?.skip ?? false;

  useLayoutEffect(() => {
    if (openedChatLoading || !openedChatReady || !openedChatGuid) {
      if (!openedChatGuid) openedChatGuidRef.current = null;
      return;
    }
    if (openedChatGuidRef.current === openedChatGuid) return;
    if (openedChatSkip) return;
    if (isGenerating) {
      openedChatGuidRef.current = openedChatGuid;
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled) return;
      const el = scroll.scrollRef.current;
      if (!el && attempts < 12) {
        attempts += 1;
        window.requestAnimationFrame(tryScroll);
        return;
      }
      openedChatGuidRef.current = openedChatGuid;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      scroll.stopScroll();
    };

    const frame = window.requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [
    isGenerating,
    openedChatGuid,
    openedChatLoading,
    openedChatReady,
    openedChatSkip,
    scroll.scrollRef,
    scroll.stopScroll,
  ]);

  // Run after every streaming render so content growth cannot restore the
  // stick-to-bottom lock while a response is being generated.
  useLayoutEffect(() => {
    if (!isGenerating) return;

    if (responseScrollQueuedRef.current) {
      responseScrollQueuedRef.current = false;
      scrollToBottom({ animation: 'instant', ignoreEscapes: true });
    } else if (!isOneTimeScrollActiveRef.current) {
      scroll.stopScroll();
    }
  });

  return {
    ...scroll,
    isAtBottom: isGenerating ? scroll.isNearBottom : scroll.isAtBottom,
    isStreamingResponseBeyondFold,
    scrollToBottom,
    scrollToResponseStart,
    stopScroll: scroll.stopScroll,
    streamingResponseRef,
  };
}
